import { useQuery } from "@blitzjs/rpc"
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Collapse,
  Box,
  CircularProgress,
  Avatar,
  Tooltip,
  Stack,
  Chip,
} from "@mui/material"
import getAbExperiments from "app/abExperiments/queries/getAbExperiments"
import Link from "next/link"
import { QueryParams } from "./AbExperimentControls"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import { useState } from "react"
import { differenceInDays } from "date-fns"

type AbExperimentListProps = {
  queryParams: QueryParams
}

const sanitizeString = (value?: string) => (value ? value.toUpperCase() : "N/A")

function Row({ experiment }) {
  const [open, setOpen] = useState(false)
  const age = differenceInDays(new Date(), new Date(experiment.createdAt))

  const totalVisits =
    experiment.versions[0]?.pipelines.reduce((sum, p) => sum + p.humanVisits, 0) || 0
  const totalComments = experiment._count.comments + experiment._count.versions
  const brandCode = sanitizeString(experiment.brand?.code)
  const geographyName = sanitizeString(experiment.geography?.name)
  const languageCode = sanitizeString(experiment.language?.code)
  const geoLang = `${geographyName}-${languageCode}`
  const url = `https://${experiment.brand.abExperimentDomain}/${experiment.geography.name}-${experiment.language.code}/${experiment.url}`

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url)
  }

  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  // Count internal pipelines and external URLs
  const pipelineCount = experiment.versions[0]?.pipelines.filter((p) => p.pipelineId).length || 0
  const externalUrlCount =
    experiment.versions[0]?.pipelines.filter((p) => p.externalUrl).length || 0
  const totalCount = experiment.versions[0]?.pipelines.length || 0

  const getPipelineCountText = () => {
    if (externalUrlCount === 0) {
      return `${pipelineCount} pipelines`
    } else if (pipelineCount === 0) {
      return `${externalUrlCount} external URLs`
    } else {
      return `${totalCount} items (${pipelineCount} pipelines, ${externalUrlCount} external)`
    }
  }

  return (
    <>
      <TableRow hover>
        <TableCell width={50}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align="left">{brandCode}</TableCell>
        <TableCell align="left">{geoLang}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Link href={`/abExperiments/${experiment.id}/show`} passHref>
              {url}
            </Link>
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            {experiment.versions[0]?.pipelines?.some((p) => p.isWinner) && (
              <Chip label="Completed" color="success" size="small" sx={{ fontWeight: "medium" }} />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={experiment.user?.email || ""}>
              <Avatar
                src={experiment.user?.avatarUrl || undefined}
                sx={{ width: 24, height: 24, fontSize: "0.875rem" }}
              >
                {getUserInitials(experiment.user?.name || "")}
              </Avatar>
            </Tooltip>
            <Typography variant="body2">{experiment.user?.name}</Typography>
          </Stack>
        </TableCell>
        <TableCell align="right">{getPipelineCountText()}</TableCell>
        <TableCell align="right">{totalVisits} visits</TableCell>
        <TableCell align="right">{totalComments}</TableCell>
        <TableCell align="right">{age} days ago</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pipeline/External URL</TableCell>
                    <TableCell align="right">Proportion</TableCell>
                    <TableCell align="right">Visits</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {experiment.versions[0]?.pipelines.map((p) => (
                    <TableRow key={p.pipelineId || p.externalUrl}>
                      <TableCell>
                        {p.pipeline ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip label="CPPv3 Pipe" size="small" variant="outlined" />
                            <Typography variant="body2">{p.pipeline.url}</Typography>
                          </Stack>
                        ) : p.externalUrl ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip label="External URL" size="small" variant="outlined" />
                            <Typography variant="body2">{p.externalUrl}</Typography>
                          </Stack>
                        ) : (
                          "Unknown"
                        )}
                      </TableCell>
                      <TableCell align="right">{p.proportion}%</TableCell>
                      <TableCell align="right">{p.humanVisits}</TableCell>
                      <TableCell align="right">
                        {p.isWinner && (
                          <Chip
                            label="Winner"
                            color="success"
                            size="small"
                            sx={{ fontWeight: "medium" }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function AbExperimentList({ queryParams }: AbExperimentListProps) {
  const [abExperiments, { isLoading }] = useQuery(getAbExperiments, queryParams, {
    suspense: false,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={50}></TableCell>
            <TableCell>Brand</TableCell>
            <TableCell>Geo-Lang</TableCell>
            <TableCell>URL</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell align="right">Pipelines</TableCell>
            <TableCell align="right">Visits</TableCell>
            <TableCell align="right">Comments</TableCell>
            <TableCell align="right">Age</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {abExperiments?.length ? (
            abExperiments.map((experiment) => <Row key={experiment.id} experiment={experiment} />)
          ) : (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                <Typography variant="body1">No experiments found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
